import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../firebase';

export const INSPECTION_OUTCOMES = Object.freeze({
  ALL_CLEAR: 'all_clear',
  MISSING_BLADES: 'missing_blades',
  DAMAGED_BLADES: 'damaged_blades',
  OTHER_HEAD_ISSUE: 'other_head_issue',
  NOT_CHECKABLE: 'not_checkable',
});

const POSITIVE_OUTCOMES = new Set([
  INSPECTION_OUTCOMES.MISSING_BLADES,
  INSPECTION_OUTCOMES.DAMAGED_BLADES,
  INSPECTION_OUTCOMES.OTHER_HEAD_ISSUE,
]);

export function normalizeInspectionOutcome(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return Object.values(INSPECTION_OUTCOMES).includes(normalized) ? normalized : null;
}

export function inspectionLabelType(outcome) {
  const normalized = normalizeInspectionOutcome(outcome);
  if (normalized === INSPECTION_OUTCOMES.ALL_CLEAR) return 'negative';
  if (POSITIVE_OUTCOMES.has(normalized)) return 'positive';
  return 'none';
}

function normalizeCount(value, { unknown = null } = {}) {
  if (value === null || value === undefined || value === '') return unknown;
  const count = Number(value);
  return Number.isFinite(count) && count >= 0 ? Math.floor(count) : unknown;
}

function normalizeCountMap(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.entries(value).reduce((result, [key, rawValue]) => {
    const head = String(key || '').trim();
    if (head) result[head] = normalizeCount(rawValue);
    return result;
  }, {});
}

export function buildGroundTruthRecord({
  branchId,
  robotSerial,
  sourceRobotId = null,
  canonicalRobotId = null,
  robotLabel,
  cycleId = null,
  parcelId = 'global',
  inspectionOutcome,
  missingBladeCount = null,
  damagedBladeCount = null,
  replacedByHead = {},
  missingByHead = {},
  damagedByHead = {},
  confirmedHeadIds = null,
  interventionId = null,
  visited = true,
  answeredAt = null,
  locationPrecision = 'ROBOT_LABEL',
  latitude = null,
  longitude = null,
  routeSegmentId = null,
  relatedNotificationId = null,
  relatedValidationCaseId = null,
  notes = '',
  user = {},
}) {
  const outcome = normalizeInspectionOutcome(inspectionOutcome);
  if (!String(branchId || '').trim() || !String(robotSerial || '').trim() || !outcome) return null;
  const replaced = normalizeCountMap(replacedByHead);
  const missing = normalizeCountMap(missingByHead);
  const damaged = normalizeCountMap(damagedByHead);
  const labelType = inspectionLabelType(outcome);
  const confirmedCount = outcome === INSPECTION_OUTCOMES.ALL_CLEAR
    ? normalizeCount(missingBladeCount, { unknown: 0 })
    : normalizeCount(missingBladeCount);
  const heads = Array.isArray(confirmedHeadIds)
    ? confirmedHeadIds.map((head) => String(head || '').trim()).filter(Boolean)
    : [...new Set([...Object.keys(missing), ...Object.keys(damaged), ...Object.keys(replaced)])];
  const headIds = [...new Set([...heads, ...Object.keys(missing), ...Object.keys(damaged), ...Object.keys(replaced)])];
  const headResults = headIds.map((headId) => {
    const inspected = heads.includes(headId) || Object.prototype.hasOwnProperty.call(missing, headId)
      || Object.prototype.hasOwnProperty.call(damaged, headId) || Object.prototype.hasOwnProperty.call(replaced, headId);
    const count = Object.prototype.hasOwnProperty.call(missing, headId)
      ? missing[headId]
      : Object.prototype.hasOwnProperty.call(replaced, headId) ? replaced[headId] : null;
    const explicitAllClear = outcome === INSPECTION_OUTCOMES.ALL_CLEAR && inspected
      && !Object.prototype.hasOwnProperty.call(damaged, headId);
    const effectiveCount = explicitAllClear && count === null ? 0 : count;
    return {
      headId,
      inspected,
      allBladesPresent: inspected && effectiveCount === 0 && !Object.prototype.hasOwnProperty.call(damaged, headId),
      confirmedMissingBladeCount: inspected ? effectiveCount : null,
      bladeState: !inspected ? 'UNKNOWN' : effectiveCount === 0 ? 'ALL_BLADES_PRESENT' : effectiveCount == null ? 'BLADE_PROBLEM_COUNT_UNKNOWN' : 'MISSING_BLADES_CONFIRMED',
      confidenceSource: 'PHYSICAL_INSPECTION',
    };
  });
  return {
    schemaVersion: 'ground-truth.v2',
    branchId: String(branchId).trim(),
    robotSerial: String(robotSerial).trim(),
    sourceRobotId: sourceRobotId ? String(sourceRobotId).trim() : null,
    canonicalRobotId: canonicalRobotId ? String(canonicalRobotId).trim() : null,
    robotLabel: String(robotLabel || robotSerial).trim(),
    cycleId: cycleId ? String(cycleId).trim() : null,
    parcelId: String(parcelId || 'global').trim(),
    inspectionTimestamp: serverTimestamp(),
    inspectionType: 'maintenance_close',
    inspectionOutcome: outcome,
    labelType,
    allBladesPresent: outcome === INSPECTION_OUTCOMES.ALL_CLEAR ? true : outcome === INSPECTION_OUTCOMES.NOT_CHECKABLE ? null : false,
    interventionId: interventionId ? String(interventionId).trim() : null,
    visited: visited === true,
    timestamp: answeredAt || serverTimestamp(),
    answeredAt: answeredAt || serverTimestamp(),
    technicianUid: String(user?.uid || '').trim() || null,
    technicianName: String(user?.displayName || user?.email || '').trim() || null,
    confirmedMissingBladeCount: confirmedCount,
    confirmedHeadIds: heads.length ? heads : null,
    headResults,
    missingBladeCount: confirmedCount,
    damagedBladeCount: normalizeCount(damagedBladeCount),
    bladeReplacedCount: Object.values(replaced).reduce((sum, count) => sum + count, 0),
    missingBladesByHead: missing,
    damagedBladesByHead: damaged,
    replacedBladesByHead: replaced,
    locationPrecision: String(locationPrecision || 'ROBOT_LABEL'),
    latitude: Number.isFinite(Number(latitude)) ? Number(latitude) : null,
    longitude: Number.isFinite(Number(longitude)) ? Number(longitude) : null,
    routeSegmentId: routeSegmentId ? String(routeSegmentId).trim() : null,
    relatedNotificationId: relatedNotificationId ? String(relatedNotificationId).trim() : null,
    relatedValidationCaseId: relatedValidationCaseId ? String(relatedValidationCaseId).trim() : null,
    notes: String(notes || '').trim(),
    source: 'maintenance-close-modal',
    labelSource: 'human_maintenance_inspection',
    reviewedByUid: String(user?.uid || '').trim(),
    reviewedByName: String(user?.displayName || user?.email || '').trim(),
    createdAt: serverTimestamp(),
  };
}

function toMillis(value) {
  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return Number.isFinite(date?.getTime?.()) ? date.getTime() : 0;
  }
  const date = new Date(value || 0);
  return Number.isFinite(date.getTime()) ? date.getTime() : 0;
}

function normalizeHeadMap(rawValue) {
  if (!rawValue || typeof rawValue !== 'object' || Array.isArray(rawValue)) return {};
  return Object.entries(rawValue).reduce((acc, [head, value]) => {
    const key = String(head || '').trim();
    if (!key) return acc;
    const numeric = Number(value?.missingBlades ?? value?.predictedMissingBlades ?? value);
    acc[key] = Number.isFinite(numeric) ? numeric : null;
    return acc;
  }, {});
}

function parseRobotSerialFromText(value) {
  const text = String(value || '').toUpperCase();
  const match = text.match(/\bBR[A-Z0-9]{7,}\b/);
  return match ? match[0].trim() : '';
}

function resolveRobotSerial(melding = {}) {
  const direct = [
    melding.robotSerial,
    melding.serial,
    melding.selectedRobot?.robotSerial,
    melding.selectedRobot?.serial,
    melding.selectedRobot?.robot?.serial,
  ]
    .map((value) => String(value || '').trim())
    .find(Boolean);

  if (direct) return direct;

  const fromRobotId = String(melding.robotId || '').trim();
  if (/^BR[A-Z0-9]{7,}$/i.test(fromRobotId)) return fromRobotId;

  return (
    parseRobotSerialFromText(melding.naam)
    || parseRobotSerialFromText(melding.robot)
    || parseRobotSerialFromText(melding.bijzonderheden)
    || ''
  );
}

export function shouldPromptMaintenanceGroundTruth({ melding, newStatus }) {
  // The real melding contract has no reliable status/resolution gate.
  // A visit plus a reported blade count is the operational trigger; the
  // server-side intervention resolver remains the authoritative writer.
  void newStatus;
  if (melding?.physicalBladeInspection?.physicallyInspected === true) return false;
  const bladeBoxOnly = melding?.bladeBoxGiven === true
    && !(Array.isArray(melding?.physicalBladeInspection?.headResults) && melding.physicalBladeInspection.headResults.length > 0)
    && !(Array.isArray(melding?.headInspectionResults) && melding.headInspectionResults.length > 0);
  if (bladeBoxOnly) return false;
  const visited = melding?.isRobotBezocht === true
    || String(melding?.robotBezocht || melding?.bezocht || '').trim().toLowerCase() === 'ja';
  return visited;
}

export async function prepareMaintenanceGroundTruthContext({ branchId, melding }) {
  const trimmedBranchId = String(branchId || '').trim();
  if (!trimmedBranchId) return null;

  const robotSerial = resolveRobotSerial(melding);
  if (!robotSerial) return null;

  // This context is used to anchor a real inspection to the complete cycle
  // history for one robot. It must not silently stop at an arbitrary 250-row
  // cap; the caller already scopes the read to one branch and serial.
  const snap = await getDocs(collection(db, 'vestigingen', trimmedBranchId, 'robots', robotSerial, 'cycleHealth'));

  const cycles = snap.docs
    .map((docSnap) => {
      const data = docSnap.data() || {};
      const prediction = data?.prediction || {};
      const analysis = data?.analysis || {};
      const predictionMap = normalizeHeadMap(prediction?.missingBladesPerHead);
      const analysisMap = normalizeHeadMap(analysis?.missingBladesPerHead);
      const perHead = Object.keys(predictionMap).length ? predictionMap : (Object.keys(analysisMap).length ? analysisMap : normalizeHeadMap(data?.predictedMissingBladesByHead));

      return {
        id: docSnap.id,
        cycleId: String(data.cycleId || docSnap.id || '').trim(),
        parcelId: String(data.parcelId || '').trim() || 'global',
        status: String(data.status || analysis.status || '').trim().toLowerCase(),
        confidence: Number(prediction?.confidence ?? data.confidence ?? 0),
        perHead,
        startedAtMs: Math.max(toMillis(data.startedAt), toMillis(data.updatedAt), toMillis(data.endedAt)),
        durationSec: Number(data.durationSec || 0),
      };
    })
    .sort((a, b) => b.startedAtMs - a.startedAtMs);

  const latest = cycles[0] || null;
  const bladeHeads = Math.max(0, Number(melding?.bladeHeads || melding?.robotBladeConfig?.bladeHeads || 0));
  const fallbackHeadNames = bladeHeads > 0
    ? Array.from({ length: bladeHeads }, (_, index) => `H${index + 1}`)
    : [];

  const headNames = latest && Object.keys(latest.perHead).length
    ? Object.keys(latest.perHead)
    : fallbackHeadNames;

  const predictedByHead = headNames.reduce((acc, headName) => {
    acc[headName] = Number(latest?.perHead?.[headName] || 0);
    return acc;
  }, {});

  return {
    branchId: trimmedBranchId,
    robotSerial,
    sourceRobotId: String(melding?.sourceRobotId || melding?.robotId || '').trim() || null,
    canonicalRobotId: String(melding?.canonicalRobotId || melding?.selectedRobot?.canonicalRobotId || '').trim() || null,
    robotLabel: String(melding?.naam || melding?.robot || robotSerial),
    cycleId: latest?.cycleId || '',
    cycleStartedAtMs: latest?.startedAtMs || Date.now(),
    parcelId: latest?.parcelId || 'global',
    headNames,
    predictedByHead,
  };
}

export async function saveMaintenanceGroundTruth({
  branchId,
  robotSerial,
  robotLabel,
  cycleId,
  parcelId,
  replacedByHead,
  missingByHead,
  damagedByHead,
  confirmedHeadIds,
  interventionId,
  visited = true,
  answeredAt,
  inspectionOutcome,
  missingBladeCount,
  damagedBladeCount,
  sourceRobotId,
  canonicalRobotId,
  relatedNotificationId,
  relatedValidationCaseId,
  notes,
  predictedByHead,
  user,
  closedAtMs,
}) {
  const normalizedBranchId = String(branchId || '').trim();
  const normalizedSerial = String(robotSerial || '').trim();
  const groundTruthRecord = buildGroundTruthRecord({
    branchId: normalizedBranchId,
    robotSerial: normalizedSerial,
    sourceRobotId,
    canonicalRobotId,
    robotLabel,
    cycleId,
    parcelId,
    inspectionOutcome,
    missingBladeCount,
    damagedBladeCount,
    replacedByHead,
    missingByHead,
    damagedByHead,
    confirmedHeadIds,
    interventionId,
    visited,
    answeredAt,
    relatedNotificationId,
    relatedValidationCaseId,
    notes,
    user,
  });
  if (!groundTruthRecord) return;

  const callableResult = await httpsCallable(functions, 'recordMaintenanceGroundTruth')({
    branchId: normalizedBranchId, robotSerial: normalizedSerial, robotLabel, cycleId, parcelId,
    inspectionOutcome, missingBladeCount, damagedBladeCount, missingByHead, damagedByHead,
    replacedByHead, confirmedHeadIds, interventionId, relatedNotificationId, relatedValidationCaseId,
    predictedByHead,
    notes,
  });
  const groundTruthId = callableResult?.data?.groundTruthId;
  if (!groundTruthId) throw new Error('Authoritative ground truth id ontbreekt na opslaan.');
  const persistedSnap = await getDoc(doc(db, 'vestigingen', normalizedBranchId, 'maintenanceGroundTruth', groundTruthId));
  if (!persistedSnap.exists()) throw new Error('Fysieke controle is niet teruggelezen na opslaan.');

  const cyclesSnap = await getDocs(
    collection(db, 'vestigingen', normalizedBranchId, 'robots', normalizedSerial, 'cycleHealth'),
  );

  const thresholdMs = Number(closedAtMs || Date.now());
  const baselineCandidate = cyclesSnap.docs
    .map((docSnap) => {
      const data = docSnap.data() || {};
      const startedAtMs = Math.max(toMillis(data.startedAt), toMillis(data.updatedAt), toMillis(data.endedAt));
      return {
        cycleId: String(data.cycleId || docSnap.id || '').trim(),
        parcelId: String(data.parcelId || parcelId || 'global').trim(),
        startedAtMs,
        durationSec: Number(data.durationSec || 0),
      };
    })
    .filter((cycle) => cycle.startedAtMs >= thresholdMs && cycle.durationSec >= 15 * 60)
    .sort((a, b) => a.startedAtMs - b.startedAtMs)[0] || null;

  if (baselineCandidate && groundTruthRecord.inspectionOutcome === INSPECTION_OUTCOMES.ALL_CLEAR) {
    const baselineDocId = `${normalizedSerial}__${baselineCandidate.parcelId}`;
    const baselinePayload = {
      robotSerial: normalizedSerial,
      parcelId: baselineCandidate.parcelId,
      branchId: normalizedBranchId,
      sourceCycleId: baselineCandidate.cycleId,
      sourceCycleStartedAtMs: baselineCandidate.startedAtMs,
      source: 'maintenance-ground-truth',
      updatedAt: serverTimestamp(),
    };

    await setDoc(doc(db, 'vestigingen', normalizedBranchId, 'knownGoodBaselines', baselineDocId), baselinePayload, { merge: true });
    await setDoc(doc(db, 'knownGoodBaselines', baselineDocId), baselinePayload, { merge: true });
  }
  return { groundTruthId, persistedRecord: { id: persistedSnap.id, ...persistedSnap.data() } };
}
